import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import enMessages from '../content/en.json';
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  getLangFromPath,
} from './languages.js';

// Re-export for convenience (several files import from here)
export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE };

const COOKIE_NAME = 'clawdius_lang';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

function getCookieLang() {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function setCookieLang(lang) {
  try {
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(lang)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
  } catch {
    // ignore
  }
}

/**
 * Detect the best initial language.
 * Priority: URL path  →  cookie  →  navigator  →  'en'
 */
function detectLanguage() {
  // 1. URL (read synchronously — React Router hasn't mounted yet)
  const fromUrl = getLangFromPath(window.location.pathname);
  if (fromUrl) return fromUrl;

  // 2. Cookie preference
  const cookie = getCookieLang();
  if (cookie && SUPPORTED_LANGUAGES.includes(cookie)) return cookie;

  // 3. navigator.language (e.g. "zh-TW" → "zh", "pt-BR" → "pt")
  const nav = (navigator.language || navigator.languages?.[0] || '').split('-')[0].toLowerCase();
  if (nav && SUPPORTED_LANGUAGES.includes(nav)) return nav;

  return DEFAULT_LANGUAGE;
}

// Lazy-load a language JSON file
async function loadMessages(lang) {
  try {
    const mod = await import(`../content/${lang}.json`);
    return mod.default;
  } catch {
    if (lang !== DEFAULT_LANGUAGE) {
      const fallback = await import(`../content/${DEFAULT_LANGUAGE}.json`);
      return fallback.default;
    }
    return {};
  }
}

// Resolve a dot-separated key path through a nested object.
// Supports array indexing with [n] notation.
function resolvePath(obj, path) {
  return path.split('.').reduce((acc, segment) => {
    if (acc == null) return undefined;
    // Array index: e.g. "steps[0]"
    const arrMatch = segment.match(/^([^[]+)\[(\d+)\]$/);
    if (arrMatch) {
      const [, key, idx] = arrMatch;
      return acc[key]?.[Number(idx)];
    }
    return acc[segment];
  }, obj);
}

// Simple interpolation: replace {key} with values[key]
function interpolate(str, values) {
  if (!values || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (values[k] != null ? values[k] : `{${k}}`));
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [lang, setLangState] = useState(() => detectLanguage());
  const [messages, setMessages] = useState(() => {
    // Synchronously initialise with English to avoid a layout-shifting
    // empty→content flash on first render (CLS fix).
    const detected = detectLanguage();
    return detected === DEFAULT_LANGUAGE ? enMessages : {};
  });
  const [loading, setLoading] = useState(() => detectLanguage() !== DEFAULT_LANGUAGE);

  // ── Sync URL → state (handles direct URL access & browser back/forward) ──
  useEffect(() => {
    const urlLang = getLangFromPath(location.pathname);
    if (urlLang && urlLang !== lang) {
      setCookieLang(urlLang);
      setLangState(urlLang);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load translation messages when language changes ──────────────────────
  useEffect(() => {
    // English is already loaded synchronously — skip the async fetch
    if (lang === DEFAULT_LANGUAGE) {
      setMessages(enMessages);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadMessages(lang).then((msgs) => {
      if (!cancelled) {
        setMessages(msgs);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [lang]);

  // ── Public API: switch language (navigates to new URL) ──────────────────
  const setLanguage = useCallback((newLang) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) return;
    setCookieLang(newLang);
    setLangState(newLang);
    // Replace the /:lang/ segment in the current path
    const newPath = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, `/${newLang}$1`);
    navigate(newPath + location.search + location.hash, { replace: true });
  }, [location, navigate]);

  // t('key.path') or t('key.path', { var: value })
  const t = useCallback((keyPath, values) => {
    const resolved = resolvePath(messages, keyPath);
    if (resolved == null) return keyPath; // fallback to key if missing
    if (typeof resolved === 'string') return interpolate(resolved, values);
    return resolved; // arrays / objects returned as-is for callers that need them
  }, [messages]);

  return (
    <I18nContext.Provider value={{ lang, setLanguage, t, loading, messages }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used inside <I18nProvider>');
  return ctx;
}
